import React, { FC, useMemo } from "react";
import classNames from "classnames";
import { PdpChecksStrip, PdpTagPill } from "../../../../../components/PdpPages/PdpChecks";
import type { PdpUrlVideoLink } from "../../../../../components/PdpPages/PdpJsonLdOutput";
import { downloadPdpScorePdf } from "../../../../../components/PdpAudit/reportFiles";
import { IconAlertGlyph, IconEyeGlyph } from "../../../videos/selectedVideoCardV2/panels";
import { StateChip } from "../../../videos/selectedVideoCardV2/performance/ScoreState";
import { hasScore } from "../../../videos/selectedVideoCardV2/performance/pageScore";
import { btnClass } from "../components/ui";
import { MarkupRow } from "./MarkupRow";
import { OurVideosRow } from "./OurVideosRow";
import { CHIP, CadenceChip, OwnerLabel, PageMenu, PageUrlLink, PausedChip, ScoreButton } from "./PageIdentity";
import { PageScoreHistory } from "./PageScoreHistory";
import { PageStatus } from "./PageStatus";
import { CAPTION, ScoreBlock } from "./ScoreBlock";
import { inSyncHistory, pageView, scoreButton, stateChip, urlParts } from "./pageModel";
import type { PdpUrl } from "./pageModel";
import { usePageScoreHistory } from "./usePageScoreHistory";

/**
 * One product page (handoff README:162-204, sheet 6): identity row, actions +
 * ⋮, the status strip for its view, the score block, then three rows —
 * Score history, Our videos on this page, Markup (JSON-LD).
 *
 * Which view it draws is `pageView` (productPages/pageModel.ts, the plan's
 * Page view table): paused · scoring · queued · delayed · failed-newer ·
 * failed-never · scored · unscored. The score block shows whenever the page
 * has a score, whatever the view — a queued, failed or paused page keeps its
 * last one on screen (IMPLEMENTATION §4). The three rows show in every view
 * except that Score history needs a score: the modal shows linked videos and
 * JSON-LD on every row (MODAL/ProductPdpUrlsPanel.tsx:588-694), so nothing
 * there is hidden behind a first score.
 *
 * The parts live beside it: PageIdentity.tsx (the identity row's pieces, the
 * score button and the ⋮), PageStatus.tsx (the status strip — the one part
 * that ticks, so a running score's counter re-renders only the strip),
 * ScoreBlock.tsx (the score and its trend).
 *
 * Layout: identity (flex 1 1 360px) and actions (1 0 auto) share a wrapping
 * row, so below ~sm the actions take their own right-aligned row (09c).
 * Tokens only; the band colours come from `scoreCls`.
 *
 * At 2+ pages the section shows one block at a time, in a tab panel; the
 * others stay mounted, hidden, so their history, open rows and a running
 * score's counter survive a tab switch. The article takes focus
 * (tabIndex -1) when the section moves focus to it: a first page just added.
 *
 * Every write is the section's (useProductPages); this renders and calls.
 */

/** The block's article id — the section focuses it, and the rows derive their ids from it. */
export const pageBlockId = (id: number) => `product-page-${id}`;

interface PageBlockProps {
  row: PdpUrl;
  productId: number;
  /** `canScoreSync(role)`: Score now vs Run report. */
  sync: boolean;
  /** `isInternal(role)`: raw error tokens, the admin video link. */
  internal: boolean;
  /**
   * The page's sectionsNonce (the settings modal closed): a history whose
   * last load failed re-reads (usePageScoreHistory).
   */
  reloadKey: number;
  scoringPageId: number | null;
  scoringStartedAt: number | null;
  refusal?: string | null;
  /** A Run report request is out for this page. */
  requesting: boolean;
  toggling: boolean;
  generating: boolean;
  generatingProduct: boolean;
  onScore: () => void;
  onToggleScoring: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onRemoveVideo: (link: PdpUrlVideoLink) => void;
  onViewAudit: (scoreId: number) => void;
  onGenerate: () => void;
  onGenerateProduct: () => void;
}

export const PageBlock: FC<PageBlockProps> = ({
  row,
  productId,
  sync,
  internal,
  reloadKey,
  scoringPageId,
  scoringStartedAt,
  refusal,
  requesting,
  toggling,
  generating,
  generatingProduct,
  onScore,
  onToggleScoring,
  onEdit,
  onDelete,
  onRemoveVideo,
  onViewAudit,
  onGenerate,
  onGenerateProduct,
}) => {
  const view = pageView(row, scoringPageId);
  const scored = hasScore(row);
  // Another block's sync score holds this one (the one-at-a-time lock).
  const locked = scoringPageId != null && scoringPageId !== row.id;
  const btn = scoreButton(sync, view, locked);
  const scoreDisabled = btn.disabled || requesting;
  const chip = stateChip(view);
  const blockId = pageBlockId(row.id);

  const history = usePageScoreHistory(productId, row.id, row.latest_score?.id, reloadKey, scored);
  const series = useMemo(() => inSyncHistory(history.history, row.latest_score), [history.history, row.latest_score]);

  return (
    <article
      id={blockId}
      tabIndex={-1}
      aria-label={`Product page ${urlParts(row.pdp_url).host || row.pdp_url}`}
      className="bg-surface border border-border-token rounded-xl px-4 py-4 sm:px-5 sm:py-[18px] flex flex-col gap-[18px] min-w-0"
    >
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3 min-w-0">
        {/* Identity */}
        <div className="flex flex-col gap-2 min-w-0" style={{ flex: "1 1 360px" }}>
          <PageUrlLink url={row.pdp_url} />
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 min-w-0">
            <OwnerLabel row={row} />
            {row.retailer && (
              <span className={classNames(CHIP, "px-2 bg-surface-2 text-text-2 uppercase")} style={{ fontSize: 10.5, letterSpacing: ".06em" }}>
                {row.retailer}
              </span>
            )}
            {view === "paused" ? <PausedChip /> : <CadenceChip interval={row.score_interval} />}
            {chip && <StateChip tone={chip.tone}>{chip.label}</StateChip>}
            {row.autopilot && (
              <span
                className={classNames(CHIP, "px-[9px] bg-tint text-accent-text")}
                style={{ fontSize: 11 }}
                title="This page is on autopilot (Autopilot tab)"
              >
                Autopilot on
              </span>
            )}
            {(row.skus || []).map((sku) => (
              <span
                key={sku}
                className={classNames(CHIP, "px-2 border border-border-token text-text-2")}
                style={{ fontSize: 10.5, letterSpacing: ".04em" }}
              >
                {sku}
              </span>
            ))}
          </div>
          {/* The basic checks, in the order things land on a page (:561-566). */}
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <PdpChecksStrip checks={row.checks} />
            <PdpTagPill checks={row.checks} />
          </div>
        </div>

        {/* Actions: right-aligned; below ~sm they wrap onto their own row.
            max-w-full: the column can't shrink (1 0 auto), so without a cap a
            refusal line sizes it to 440px and pushes a 375px page sideways. */}
        <div className="flex flex-col items-end gap-1.5 min-w-0 max-w-full" style={{ flex: "1 0 auto" }}>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {scored && (
              <button
                type="button"
                onClick={() => onViewAudit(row.latest_score?.id as number)}
                className={btnClass("secondary")}
              >
                <IconEyeGlyph size={15} strokeWidth={1.8} />
                View audit
              </button>
            )}
            {/* The ⋮ keeps to the button beside it: at 375 the three don't fit
                one row, and the ⋮ alone would wrap onto a row of its own. A
                paused page hides Score now, so Resume scoring is that button. */}
            <div className="flex items-center gap-2 flex-nowrap">
              {view === "paused" && (
                <button type="button" onClick={onToggleScoring} disabled={toggling} className={btnClass("secondary", { disabled: toggling })}>
                  Resume scoring
                </button>
              )}
              {!btn.hidden && <ScoreButton label={btn.label} disabled={scoreDisabled} onClick={onScore} />}
              <PageMenu
                paused={view === "paused"}
                toggling={toggling}
                onToggleScoring={onToggleScoring}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            </div>
          </div>
          {btn.note && (
            <span className="text-text-2 text-right" style={CAPTION}>
              {btn.note}
            </span>
          )}
          {refusal && (
            // Capped at 440px (and at the row, via the column's max-w-full), so
            // a long plan-limit line wraps rather than widening the actions
            // column past the identity beside it.
            <span role="alert" className="inline-flex items-start gap-1.5 max-w-[440px] text-down text-right" style={{ fontSize: 12 }}>
              <span className="inline-flex shrink-0 mt-px">
                <IconAlertGlyph size={13} strokeWidth={1.8} />
              </span>
              {refusal}
            </span>
          )}
        </div>
      </div>

      <PageStatus
        row={row}
        view={view}
        sync={sync}
        scored={scored}
        internal={internal}
        scoringStartedAt={scoringStartedAt}
        scoreDisabled={scoreDisabled}
        onScore={onScore}
      />

      {scored && <ScoreBlock row={row} view={view} series={series} />}

      <div className="flex flex-col min-w-0">
        {scored && (
          <PageScoreHistory
            blockId={blockId}
            history={history.history}
            loading={history.loading}
            error={history.error}
            onRetry={history.reload}
            onView={onViewAudit}
            onPdf={(scoreId) => downloadPdpScorePdf({ productId, pdpUrlId: row.id, scoreId, url: row.pdp_url })}
          />
        )}
        <OurVideosRow row={row} blockId={blockId} internal={internal} onRemove={onRemoveVideo} />
        <MarkupRow
          row={row}
          blockId={blockId}
          generating={generating}
          generatingProduct={generatingProduct}
          onGenerate={onGenerate}
          onGenerateProduct={onGenerateProduct}
        />
      </div>
    </article>
  );
};
